// src/components/SystemRecommendation/components/FitsYourBankAccount.tsx

import React from 'react';
import { FinancialFitData, ViewMode } from '../types';
import CollapsibleSection from './CollapsibleSection';

interface FitsYourBankAccountProps {
  data: FinancialFitData;
  viewMode: ViewMode;
}

const FitsYourBankAccount: React.FC<FitsYourBankAccountProps> = ({ data, viewMode }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <CollapsibleSection
      title="This is how it fits your bank account"
      icon="💰"
      confidenceLevel={data.confidence.level}
      summary={data.confidence.summary}
    >
      {viewMode === 'customer' ? (
        <div className="customer-content">
          <div className="fit-section">
            <h4>Upfront Investment</h4>
            <div className="cost-breakdown">
              <div className="cost-item">
                <span>Equipment</span>
                <strong>{formatCurrency(data.upfrontCosts.equipment)}</strong>
              </div>
              <div className="cost-item">
                <span>Installation</span>
                <strong>{formatCurrency(data.upfrontCosts.installation)}</strong>
              </div>
              {data.upfrontCosts.additional.map((item, i) => (
                <div key={i} className="cost-item">
                  <span>{item.item}</span>
                  <strong>{formatCurrency(item.cost)}</strong>
                </div>
              ))}
              <div className="cost-item total">
                <span>Total</span>
                <strong>{formatCurrency(data.upfrontCosts.total)}</strong>
              </div>
            </div>
          </div>

          <div className="fit-section">
            <h4>Monthly Running Costs</h4>
            <div className="savings-comparison">
              <div className="cost-comparison">
                <div className="comparison-item old">
                  <span className="label">Current system</span>
                  <span className="amount">{formatCurrency(data.runningCosts.currentAnnual / 12)}/month</span>
                </div>
                <div className="comparison-arrow">→</div>
                <div className="comparison-item new">
                  <span className="label">New system</span>
                  <span className="amount">{formatCurrency(data.runningCosts.newAnnual / 12)}/month</span>
                </div>
              </div>
              <div className="savings-highlight">
                <strong>Save {formatCurrency(data.runningCosts.monthlySaving)}/month</strong>
                <span className="detail-text">
                  ({formatCurrency(data.runningCosts.monthlySaving * 12)}/year)
                </span>
              </div>
            </div>
          </div>

          <div className="fit-section">
            <h4>Payback Period</h4>
            <p>
              <strong>Break even in {data.payback.years} years</strong>
            </p>
            <p className="detail-text">
              Your savings will cover the upfront cost by year {data.payback.breakEvenYear}
            </p>
          </div>

          {data.financing.available && data.financing.monthlyPayment && (
            <div className="fit-section">
              <h4>Finance Options</h4>
              <p>
                <strong>Monthly payment:</strong> {formatCurrency(data.financing.monthlyPayment)} 
                {data.financing.term && ` over ${data.financing.term} months`}
              </p>
              {data.financing.apr && (
                <p className="detail-text">Representative APR: {data.financing.apr}%</p>
              )}
            </div>
          )}

          {data.incentives.available.length > 0 && (
            <div className="fit-section">
              <h4>Available Grants & Incentives</h4>
              <ul>
                {data.incentives.available.map((incentive, i) => (
                  <li key={i}>✓ {incentive}</li>
                ))}
              </ul>
              <p className="success-text">
                <strong>Total potential value: {formatCurrency(data.incentives.totalValue)}</strong>
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="technical-content">
          <div className="tech-grid">
            <div className="tech-item">
              <span className="tech-label">Equipment Cost:</span>
              <span className="tech-value">{formatCurrency(data.upfrontCosts.equipment)}</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Installation Cost:</span>
              <span className="tech-value">{formatCurrency(data.upfrontCosts.installation)}</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Total Upfront:</span>
              <span className="tech-value">{formatCurrency(data.upfrontCosts.total)}</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Current Annual Cost:</span>
              <span className="tech-value">{formatCurrency(data.runningCosts.currentAnnual)}</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">New Annual Cost:</span>
              <span className="tech-value">{formatCurrency(data.runningCosts.newAnnual)}</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Annual Saving:</span>
              <span className="tech-value">{formatCurrency(data.runningCosts.monthlySaving * 12)}</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Payback Period:</span>
              <span className="tech-value">{data.payback.years} years</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Break Even Year:</span>
              <span className="tech-value">Year {data.payback.breakEvenYear}</span>
            </div>
            {data.financing.available && data.financing.monthlyPayment && (
              <>
                <div className="tech-item">
                  <span className="tech-label">Finance Available:</span>
                  <span className="tech-value">
                    {formatCurrency(data.financing.monthlyPayment)}/month
                  </span>
                </div>
                {data.financing.apr && (
                  <div className="tech-item">
                    <span className="tech-label">APR:</span>
                    <span className="tech-value">{data.financing.apr}%</span>
                  </div>
                )}
              </>
            )}
            {data.incentives.totalValue > 0 && (
              <div className="tech-item">
                <span className="tech-label">Available Incentives:</span>
                <span className="tech-value">{formatCurrency(data.incentives.totalValue)}</span>
              </div>
            )}
          </div>
          {data.upfrontCosts.additional.length > 0 && (
            <div className="tech-notes">
              <p><strong>Additional costs:</strong></p>
              <ul>
                {data.upfrontCosts.additional.map((item, i) => (
                  <li key={i}>{item.item}: {formatCurrency(item.cost)}</li>
                ))}
              </ul>
            </div>
          )}
          {data.incentives.available.length > 0 && (
            <div className="tech-notes">
              <p><strong>Available incentives:</strong></p>
              <ul>
                {data.incentives.available.map((incentive, i) => (
                  <li key={i}>{incentive}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </CollapsibleSection>
  );
};

export default FitsYourBankAccount;
